import {
  Alert,
  Badge,
  Card,
  Group,
  Image,
  Stack,
  Text,
  Title,
} from '@mantine/core'
import { PriceDisplay } from '@markku/components'
import { type VisitWithRelations } from '@markku/types'
import {
  addVat,
  calculateFinalPrice,
  formatDate,
  formatEuros,
  partition,
  sortBy,
  sum,
  sumBy,
  VAT_LABEL,
} from '@markku/utils'
import { IconCheck, IconFileInvoice, IconInfoCircle } from '@tabler/icons-react'
import { Trans, useLingui } from '@lingui/react/macro'
import { InvoiceItem } from './InvoiceItem'
import faceImage from '$src/assets/images/face.png'

interface VisitPaymentCardProps {
  visit: VisitWithRelations
}

export function VisitPaymentCard({ visit }: VisitPaymentCardProps) {
  const { t } = useLingui()

  // Hide payment card for einvoice customers (billed manually via Fennoa)
  if (visit.property?.customer?.billingMethod === 'einvoice') {
    return null
  }

  // Get all invoices sorted by createdAt descending (most recent first)
  const sortedInvoices = sortBy(visit.invoices || [], [
    (inv) => inv.createdAt,
    'desc',
  ])
  const hasInvoices = sortedInvoices.length > 0

  // Only show for visits with separately invoiced tasks that have a billable amount
  const billableTasks =
    visit.visitTasks?.filter(
      (task) =>
        task.billingType === 'separately_invoiced' &&
        calculateFinalPrice(task.price, task.discountPercent) > 0,
    ) || []

  // Filter line items: show billable and not_billable (exclude tbd), must have a price
  const lineItems = (visit.visitLineItems || []).filter(
    (item) =>
      item.billingType !== 'tbd' &&
      item.unitPriceVat0 !== null &&
      item.unitPriceVat0 !== undefined,
  )

  const lineItemsTotal = sumBy(
    lineItems.filter((item) => item.billingType === 'billable'),
    (item) => addVat(parseFloat(item.unitPriceVat0 as string)) * item.quantity,
  )

  // Don't show if no billable tasks AND no invoices AND no line items
  if (!billableTasks.length && !hasInvoices && !lineItems.length) {
    return null
  }

  const hasSubscription = !!visit.subscription

  // Split tasks into paid, billed (invoiced but not paid), and unbilled
  const [paidTasks, notPaidTasks] = partition(
    billableTasks,
    (task) => !!task.paidAt,
  )
  const [billedTasks, unbilledTasks] = partition(
    notPaidTasks,
    (task) => !!task.billedAt,
  )

  const allPaid = notPaidTasks.length === 0
  const partiallyPaid = paidTasks.length > 0 && notPaidTasks.length > 0

  // Calculate totals
  const paidTotal = sum(
    paidTasks.map((task) =>
      calculateFinalPrice(task.price, task.discountPercent),
    ),
  )
  const unbilledTotal = sum(
    unbilledTasks.map((task) =>
      calculateFinalPrice(task.price, task.discountPercent),
    ),
  )

  const allTasksTotal = sum(
    billableTasks.map((task) =>
      calculateFinalPrice(task.price, task.discountPercent),
    ),
  )
  const grandTotal = allTasksTotal + lineItemsTotal

  const paidDate = paidTasks[0]?.paidAt

  return (
    <Card withBorder>
      <Stack gap="md">
        <Group justify="space-between" align="center">
          <Title order={4}>
            {hasSubscription ? (
              <Trans>Additional work</Trans>
            ) : (
              <Trans>Payment</Trans>
            )}
          </Title>
          {allPaid && paidDate && (
            <Badge color="green" size="lg">
              <Trans>Paid {formatDate(paidDate)}</Trans>
            </Badge>
          )}
          {partiallyPaid && (
            <Badge color="orange" size="lg">
              <Trans>Partially paid</Trans>
            </Badge>
          )}
        </Group>

        {/* Non-subscriber - invoices (show all, including voided) */}
        {!hasSubscription && hasInvoices && (
          <Stack gap="xs">
            <Text size="sm" fw={500} c="dimmed">
              {sortedInvoices.length > 1 ? (
                <Trans>Invoices</Trans>
              ) : (
                <Trans>Invoice</Trans>
              )}
            </Text>
            {sortedInvoices.map((invoice) => (
              <InvoiceItem key={invoice.id} invoice={invoice} />
            ))}
          </Stack>
        )}

        {/* Fair billing message - shown after invoices for non-subscribers with unpaid invoices */}
        {!hasSubscription && hasInvoices && !allPaid && (
          <Group justify="center" gap={8} py={4}>
            <Image
              src={faceImage}
              alt="Markku"
              w={22}
              h={22}
              style={{ objectFit: 'contain', flexShrink: 0 }}
            />
            <Text size="xs" c="dimmed" style={{ lineHeight: 1.4 }}>
              <Trans>
                We don't charge an invoicing fee. Thank you for paying on time!
              </Trans>
            </Text>
          </Group>
        )}

        {/* Subscriber - unpaid tasks */}
        {hasSubscription && notPaidTasks.length > 0 && (
          <Alert
            icon={<IconInfoCircle size={16} />}
            title={t`Additional work will be added to your next monthly invoice automatically`}
            color="blue"
          >
            {partiallyPaid
              ? t`New additional work will be added automatically to your next monthly invoice. You don't need to do anything.`
              : t`The additional work listed below will be added automatically to your next monthly invoice. You don't need to do anything.`}
          </Alert>
        )}

        {/* Paid tasks section */}
        {paidTasks.length > 0 && (
          <Stack gap="xs">
            <Group justify="space-between">
              <Text size="sm" fw={600} c="dimmed">
                <Trans>Paid work:</Trans>
              </Text>
              {paidDate && (
                <Text size="xs" c="dimmed">
                  {formatDate(paidDate)}
                </Text>
              )}
            </Group>
            {paidTasks.map((task) => (
              <Group key={task.id} justify="space-between" wrap="nowrap">
                <Group gap="xs" style={{ flex: 1 }}>
                  <IconCheck size={16} color="var(--mantine-color-green-6)" />
                  <Text size="sm" c="dimmed">
                    {task.taskName}
                  </Text>
                </Group>
                <PriceDisplay
                  basePrice={task.price}
                  discountPercent={task.discountPercent}
                  size="sm"
                />
              </Group>
            ))}
            {partiallyPaid && (
              <Group justify="space-between" pt="xs">
                <Text size="sm" fw={600} c="dimmed">
                  <Trans>Total paid</Trans>
                </Text>
                <Text size="sm" fw={600} c="dimmed">
                  {paidTotal.toFixed(2)}€
                </Text>
              </Group>
            )}
          </Stack>
        )}

        {/* Billed tasks section (invoiced but not paid) */}
        {billedTasks.length > 0 && (
          <Stack gap="xs">
            <Text size="sm" fw={600} c="dimmed">
              <Trans>Invoiced work:</Trans>
            </Text>
            {billedTasks.map((task) => (
              <Group key={task.id} justify="space-between" wrap="nowrap">
                <Group gap="xs" style={{ flex: 1 }}>
                  <IconFileInvoice
                    size={16}
                    color="var(--mantine-color-blue-6)"
                  />
                  <Text size="sm">{task.taskName}</Text>
                </Group>
                <PriceDisplay
                  basePrice={task.price}
                  discountPercent={task.discountPercent}
                  size="sm"
                />
              </Group>
            ))}
          </Stack>
        )}

        {/* Unbilled tasks section (not yet invoiced) */}
        {unbilledTasks.length > 0 && (
          <Stack gap="xs">
            <Text size="sm" fw={600} c="dimmed">
              <Trans>Uninvoiced work:</Trans>
            </Text>
            {unbilledTasks.map((task) => (
              <Group key={task.id} justify="space-between" wrap="nowrap">
                <Text size="sm" style={{ flex: 1 }}>
                  {task.taskName}
                </Text>
                <PriceDisplay
                  basePrice={task.price}
                  discountPercent={task.discountPercent}
                  size="sm"
                />
              </Group>
            ))}

            <Group justify="space-between" pt="xs">
              <Text size="sm" fw={600} c="dimmed">
                <Trans>Uninvoiced total</Trans>
              </Text>
              <Text size="sm" fw={600} c="dimmed">
                {unbilledTotal.toFixed(2)}€
              </Text>
            </Group>
            {!hasSubscription && (
              <Group gap={4}>
                <IconInfoCircle
                  size={14}
                  color="var(--mantine-color-blue-6)"
                  style={{ flexShrink: 0 }}
                />
                <Text size="xs" c="blue.6">
                  <Trans>
                    Invoice will be sent shortly. Payment period is 14 days.
                  </Trans>
                </Text>
              </Group>
            )}
          </Stack>
        )}

        {/* Products and services (visit line items) */}
        {lineItems.length > 0 && (
          <Stack gap="xs">
            <Text size="sm" fw={600} c="dimmed">
              <Trans>Products and services:</Trans>
            </Text>
            {lineItems.map((item) => {
              const isBillable = item.billingType === 'billable'
              const priceWithVat = isBillable
                ? addVat(parseFloat(item.unitPriceVat0 as string)) *
                  item.quantity
                : 0

              return (
                <Group key={item.id} justify="space-between" wrap="nowrap">
                  <Text size="sm" style={{ flex: 1 }}>
                    {item.quantity > 1
                      ? `${item.quantity} × ${item.name}`
                      : item.name}
                  </Text>
                  <Text size="sm" fw={500}>
                    {formatEuros(priceWithVat)}
                  </Text>
                </Group>
              )
            })}
          </Stack>
        )}

        {/* Grand total */}
        <Group justify="space-between" pt="xs">
          <Stack gap={0}>
            <Text size="sm" fw={600}>
              <Trans>Total</Trans>
            </Text>
            <Text size="xs" c="dimmed">
              {VAT_LABEL}
            </Text>
          </Stack>
          <Text size="sm" fw={600}>
            {formatEuros(grandTotal)}
          </Text>
        </Group>
      </Stack>
    </Card>
  )
}
